import { useCallback, useState } from 'react'
import req from '../api/request'

/**
 * Image format from the API
 */
export interface ImageFormat {
  id: string
  filename: string
  full_path: string
  mime_type: string
  width: number
  height: number
}

/**
 * Image data type from the API
 */
export interface Image {
  id: string
  filename: string
  full_path: string
  mime_type: string
  width: number
  height: number
  formats: ImageFormat[]
}

/**
 * Organization data type
 */
export interface Organization {
  id: string
  name: string
  abbreviation: string
  image: Image | null
}

/**
 * Player type data
 */
export interface PlayerType {
  id: string
  name: string
  name_full: string
  abbreviation: string
  is_goalie: number
}

/**
 * Player data
 */
export interface Player {
  id: string
  name_first: string
  name_middle: string | null
  name_last: string
  player_number: number | null
  play_direction: string | null
  is_affiliate: boolean
  is_injured: boolean
  is_playing: boolean
  is_starting: boolean
  is_proposed_starting: boolean
  is_suspended: boolean
  attendance_status: string | null
  customer_identifier: string
  sf_id: string | null
  suspensions: any[]
  is_signed_waiver: boolean
  league_registration_number: string | null
  player_type: PlayerType
  image?: Image
  full_name?: string | null
  birth_date?: string | null
  height?: string | null
  weight?: string | null
  created_at?: string
  gender?: string | null
  email?: string
  phone_number?: string | null
  invitation_id?: string | null
  is_accepted?: boolean
  is_invited?: boolean
}

/**
 * Team Official data
 */
export interface TeamOfficial {
  id: string
  name_first: string
  name_middle: string | null
  name_last: string
  name_full: string
  team_official_type: number
  full_name: string | null
  birth_date: string | null
  phone: string | null
  created_at: string
  gender: any | null
  email: string | null
  phone_number: string | null
  invitation_id: string | null
  is_accepted: boolean
  is_invited: boolean
  image?: Image
}

/**
 * Roster data type
 */
export interface Roster {
  id: string
  name: string
  schedule: string
  schedule_uid: string
  updated_at: string
  should_show_waiver: boolean
  is_registration_on: boolean
  players: Player[]
  players_count: number
  team_officials: TeamOfficial[]
  team_officials_count: number
}

/**
 * Hierarchy item
 */
export interface HierarchyItem {
  uid: string
  name: string
  abbreviation: string
}

/**
 * Team data type
 */
export interface Team {
  id: string
  name: string
  name_full: string | null
  abbreviation: string | null
  email: string | null
  website: string | null
  phone: string | null
  support_email: string | null
  support_website: string | null
  support_phone: string | null
  is_public: boolean
  is_visible: boolean
  sport_id: string
  organization_id: string
  updated_at: string
  image: Image | null
  is_favorite: boolean
  rosters: Roster[]
  hierarchy: HierarchyItem[]
  daysmart_id: string | null
  salesforce_id: string | null
  generic_sport_id: string | null
}

/**
 * API pagination metadata
 */
export interface PaginationMeta {
  total: number
  count: number
  per_page: number
  current_page: number
  total_pages: number
}

/**
 * Team search response type
 */
export interface TeamSearchResponse {
  data: Team[]
  meta: {
    pagination: PaginationMeta
  }
}

/**
 * Search parameters for team search
 */
export interface TeamSearchParams {
  query: string
  organization_id?: string
  page?: number
}

/**
 * Hook result interface
 */
interface UseSearchAdminTeamsResult {
  results: Team[] | null
  loading: boolean
  error: Error | null
  meta: PaginationMeta | null
  search: (params: TeamSearchParams) => Promise<void>
  clearResults: () => void
  currentPage: number
  totalPages: number
  nextPage: () => Promise<void>
  prevPage: () => Promise<void>
  goToPage: (page: number) => Promise<void>
  hasNextPage: boolean
  hasPrevPage: boolean
  currentSearchParams: TeamSearchParams | null
}

/**
 * Custom hook for searching teams in the admin interface
 *
 * @returns Object containing search results, loading state, error state, pagination metadata, pagination functions, and search function
 *
 * @example
 * ```tsx
 * const {
 *   teams,
 *   loading,
 *   error,
 *   meta,
 *   search,
 *   clearResults,
 *   nextPage,
 *   prevPage,
 *   currentPage,
 *   totalPages,
 *   hasNextPage,
 *   hasPrevPage
 * } = useSearchAdminTeams()
 *
 * useEffect(() => {
 *   search({ query: 'dragons', organization_id: 'bVpPSnNx6BsfvRBM' })
 * }, [search])
 *
 * // To navigate through pages:
 * const handleNextPage = () => {
 *   if (hasNextPage) {
 *     nextPage()
 *   }
 * }
 *
 * // To clear results:
 * const handleClear = () => {
 *   clearResults()
 * }
 * ```
 */
export const useSearchAdminTeams = (): UseSearchAdminTeamsResult => {
  const [teams, setTeams] = useState<Team[] | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [currentSearchParams, setCurrentSearchParams] =
    useState<TeamSearchParams | null>(null)

  /**
   * Main search function to query the API
   */
  const search = useCallback(async (params: TeamSearchParams) => {
    setLoading(true)
    setError(null)
    try {
      // Build query parameters
      const queryParams = new URLSearchParams()
      queryParams.append('query', params.query)

      if (params.organization_id) {
        queryParams.append('organization_id', params.organization_id)
      }

      if (params.page) {
        queryParams.append('page', params.page.toString())
      }

      // Make the API request
      const response = await req(
        `/search/admin/teams?${queryParams.toString()}`
      )

      setTeams(response.data)
      setMeta(response.meta.pagination)
      setCurrentSearchParams(params)
    } catch (err: any) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred during team search')
      )
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Navigate to the next page of results
   */
  const nextPage = useCallback(async () => {
    if (
      !currentSearchParams ||
      !meta ||
      meta.current_page >= meta.total_pages
    ) {
      return
    }

    await search({
      ...currentSearchParams,
      page: meta.current_page + 1,
    })
  }, [currentSearchParams, meta, search])

  /**
   * Navigate to the previous page of results
   */
  const prevPage = useCallback(async () => {
    if (!currentSearchParams || !meta || meta.current_page <= 1) {
      return
    }

    await search({
      ...currentSearchParams,
      page: meta.current_page - 1,
    })
  }, [currentSearchParams, meta, search])

  /**
   * Navigate to a specific page of results
   */
  const goToPage = useCallback(
    async (page: number) => {
      if (!currentSearchParams || !meta) {
        return
      }

      if (page < 1 || page > meta.total_pages) {
        console.warn(`Page ${page} is out of range (1-${meta.total_pages})`)
        return
      }

      await search({
        ...currentSearchParams,
        page,
      })
    },
    [currentSearchParams, meta, search]
  )

  /**
   * Clear search results and reset state
   */
  const clearResults = useCallback(() => {
    setTeams(null)
    setMeta(null)
    setCurrentSearchParams(null)
    setError(null)
  }, [])

  // Derived values for pagination state
  const currentPage = meta?.current_page || 1
  const totalPages = meta?.total_pages || 1
  const hasNextPage = meta ? currentPage < meta.total_pages : false
  const hasPrevPage = meta ? currentPage > 1 : false

  return {
    results: teams,
    loading,
    error,
    meta,
    search,
    clearResults,
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    goToPage,
    hasNextPage,
    hasPrevPage,
    currentSearchParams,
  }
}
